import { getDefaultTenantId } from './query-context';
import { supabase } from './query-client';
import { toCamel } from './to-camel';

// ─── Facilities ─────────────────────────────────────────────

export async function fetchFacilities(tenantId?: string) {
  const sb = supabase();
  const tid = tenantId ?? (await getDefaultTenantId());
  const { data, error } = await sb
    .from('facilities')
    .select(
      'id, name, facility_type, status, suburb, state, postcode, phone, email, total_beds, operating_beds, manager_id, region_id, latitude, longitude, timezone, created_at',
    )
    .eq('tenant_id', tid)
    .in('status', ['ACTIVE', 'SETUP'])
    .order('name')
    .limit(500);
  if (error) throw error;
  return toCamel(data);
}

/**
 * Fetch facilities scoped to the current user's access level.
 * SYSTEM_ADMIN / TENANT_ADMIN see all tenant facilities.
 * Other roles see only facilities assigned via staff_facilities junction.
 */
export async function fetchUserFacilities(
  tenantId: string,
  userId: string,
  role: string,
): Promise<
  Array<{
    id: string;
    name: string;
    facilityType: string;
    status: string;
    suburb: string | null;
    state: string;
    timezone?: string;
    regionId?: string | null;
  }>
> {
  // Admins see everything
  if (['SYSTEM_ADMIN', 'TENANT_ADMIN'].includes(role)) {
    return fetchFacilities(tenantId);
  }

  const sb = supabase();

  // Staff.user_id stores the canonical public.users.id, not the auth UID.
  // Resolve the caller through users first, then fall back to email-based
  // recovery for partially migrated/test accounts.
  let publicUserId: string | null = null;
  let callerEmail: string | null = null;

  // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null triggers email-based recovery on next branch
  const { data: publicUser } = await sb
    .from('users')
    .select('id, email')
    .eq('tenant_id', tenantId)
    .eq('auth0_id', userId)
    .maybeSingle();

  if (publicUser) {
    publicUserId = String(publicUser.id);
    callerEmail = typeof publicUser.email === 'string' ? publicUser.email : null;
  } else {
    const {
      data: { user },
    } = await sb.auth.getUser();
    callerEmail = user?.email ?? null;

    if (callerEmail) {
      // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null falls through to staff lookup below
      const { data: publicUserByEmail } = await sb
        .from('users')
        .select('id, email')
        .eq('tenant_id', tenantId)
        .eq('email', callerEmail)
        .maybeSingle();

      if (publicUserByEmail) {
        publicUserId = String(publicUserByEmail.id);
        callerEmail =
          typeof publicUserByEmail.email === 'string' ? publicUserByEmail.email : callerEmail;
      }
    }
  }

  let staffRecord: { id: string } | null = null;

  if (publicUserId) {
    // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null falls through to email-based lookup below
    const { data } = await sb
      .from('staff')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('user_id', publicUserId)
      .maybeSingle();
    staffRecord = data;
  }

  if (!staffRecord && callerEmail) {
    // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null exhausts lookups, returns [] for access denied
    const { data } = await sb
      .from('staff')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('email', callerEmail)
      .maybeSingle();
    staffRecord = data;
  }

  if (!staffRecord) return [];

  const { data: assignments, error } = await sb
    .from('staff_facilities')
    .select('facility_id, is_primary')
    .eq('staff_id', staffRecord.id)
    .eq('tenant_id', tenantId);

  if (error || !assignments) return [];

  const facilityIds = assignments
    .map((a: any) => (typeof a.facility_id === 'string' ? a.facility_id : null))
    .filter(Boolean)
    .filter((id: string, index: number, ids: string[]) => ids.indexOf(id) === index);

  if (facilityIds.length === 0) return [];

  const { data: facilities, error: facilitiesError } = await sb
    .from('facilities')
    .select('id, name, facility_type, status, suburb, state, region_id, timezone')
    .eq('tenant_id', tenantId)
    .in('id', facilityIds);

  if (facilitiesError || !facilities) return [];

  return facilities
    .filter((f: any) => f.status === 'ACTIVE' || f.status === 'SETUP')
    .map((f: any) => toCamel(f));
}

/**
 * Fetch home-care/SAH service areas scoped to the current user's access level.
 * SYSTEM_ADMIN / TENANT_ADMIN see all tenant service areas.
 * Other roles see only areas assigned via staff_service_areas.
 */
export async function fetchUserServiceAreas(
  tenantId: string,
  userId: string,
  role: string,
): Promise<
  Array<{
    id: string;
    name: string;
    status: string;
    facilityId: string | null;
    facilityName?: string | null;
    suburb?: string | null;
    state?: string | null;
  }>
> {
  const sb = supabase();
  const selectableStatuses = ['ACTIVE', 'SETUP', 'FULL'];

  const mapServiceArea = (area: any) => {
    const facility = area?.facility ?? area?.facilities ?? null;
    return {
      id: String(area.id),
      name: String(area.name),
      status: String(area.status ?? 'ACTIVE'),
      facilityId: area.facility_id ? String(area.facility_id) : null,
      facilityName: facility?.name ?? null,
      suburb: facility?.suburb ?? null,
      state: facility?.state ?? null,
    };
  };

  if (['SYSTEM_ADMIN', 'TENANT_ADMIN'].includes(role)) {
    const { data, error } = await sb
      .from('service_areas')
      .select('id, name, status, facility_id, facility:facilities(id, name, suburb, state)')
      .eq('tenant_id', tenantId)
      .in('status', selectableStatuses)
      .order('name')
      .limit(500);

    if (error || !data) return [];
    return data.map(mapServiceArea);
  }

  let publicUserId: string | null = null;
  let callerEmail: string | null = null;

  // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null triggers email-based recovery on next branch
  const { data: publicUser } = await sb
    .from('users')
    .select('id, email')
    .eq('tenant_id', tenantId)
    .eq('auth0_id', userId)
    .maybeSingle();

  if (publicUser) {
    publicUserId = String(publicUser.id);
    callerEmail = typeof publicUser.email === 'string' ? publicUser.email : null;
  } else {
    const {
      data: { user },
    } = await sb.auth.getUser();
    callerEmail = user?.email ?? null;

    if (callerEmail) {
      // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null falls through to staff lookup below
      const { data: publicUserByEmail } = await sb
        .from('users')
        .select('id, email')
        .eq('tenant_id', tenantId)
        .eq('email', callerEmail)
        .maybeSingle();

      if (publicUserByEmail) {
        publicUserId = String(publicUserByEmail.id);
        callerEmail =
          typeof publicUserByEmail.email === 'string' ? publicUserByEmail.email : callerEmail;
      }
    }
  }

  let staffRecord: { id: string } | null = null;

  if (publicUserId) {
    // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null falls through to email-based lookup below
    const { data } = await sb
      .from('staff')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('user_id', publicUserId)
      .maybeSingle();
    staffRecord = data;
  }

  if (!staffRecord && callerEmail) {
    // eslint-disable-next-line statura/no-silent-maybe-single -- resolver chain: null exhausts lookups, returns [] for access denied
    const { data } = await sb
      .from('staff')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('email', callerEmail)
      .maybeSingle();
    staffRecord = data;
  }

  if (!staffRecord) return [];

  const { data: assignments, error } = await sb
    .from('staff_service_areas')
    .select(
      'service_area_id, is_primary, service_areas(id, name, status, facility_id, facility:facilities(id, name, suburb, state))',
    )
    .eq('staff_id', staffRecord.id)
    .eq('tenant_id', tenantId);

  if (error || !assignments) return [];

  return assignments
    .map((assignment: any) => assignment.service_areas)
    .filter(Boolean)
    .filter((area: any) => selectableStatuses.includes(String(area.status ?? 'ACTIVE')))
    .map(mapServiceArea);
}
